import { SubsessionStatus, SubsessionAllocationPattern } from 'zoom/subsessionSdkEnums'
import { WseUserId } from 'api/types'
import { UseSubsessionReturn } from './useSubsession'
import { ConferenceParticipant } from 'contexts/conference/types'
import { rollbarService, RollbarExceptionNames } from 'services/rollbar'
import { MAX_ROOMS, STORAGE_KEY_BREAKOUT_ROOM, SUBSESSION_DELAYS } from './subsessionConstants'

/* cSpell:words subsession Subsession zoomvideo */

export const persistActiveRoom = (roomId: number): void => {
    try {
        sessionStorage.setItem(STORAGE_KEY_BREAKOUT_ROOM, String(roomId))
    } catch {
        // sessionStorage unavailable (private mode, quota, etc.)
    }
}

export const clearActiveRoom = (): void => {
    try {
        sessionStorage.removeItem(STORAGE_KEY_BREAKOUT_ROOM)
    } catch {
        // ignore
    }
}

export const readActiveRoom = (): number | null => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY_BREAKOUT_ROOM)
        if (raw === null) return null
        const parsed = Number(raw)
        return Number.isInteger(parsed) && parsed >= 0 && parsed < 4 ? parsed : null
    } catch {
        return null
    }
}

export const subsessionNameToRoomId = (name: string | undefined): number | null => {
    if (!name) return null
    const match = name.match(/^Room ([A-D])$/)
    if (!match) return null
    return match[1].charCodeAt(0) - 65
}

/**
 * Orchestrates the subsession start sequence.
 */
export const startSubsessionWorkflow = async (params: {
    roomCount: number
    usersRooms: Array<{ userId: WseUserId; roomId: number; indexId: number }>
    isTeacher: boolean
    wasActive: boolean
    subsession: UseSubsessionReturn
    ssClient: any
    conference: any
}): Promise<boolean> => {
    const { usersRooms, ssClient, conference } = params

    if (!ssClient) {
        return false
    }

    try {
        const status = ssClient.getSubsessionStatus()
        let subsessionList = ssClient.getSubsessionList() || []

        if (status === SubsessionStatus.NotStarted || subsessionList.length === 0) {
            const labels = Array.from({ length: MAX_ROOMS }, (_, i) => `Room ${String.fromCharCode(65 + i)}`)
            subsessionList = await ssClient.createSubsessions(labels, SubsessionAllocationPattern.Manually)

            if (!Array.isArray(subsessionList) || subsessionList.length === 0) {
                rollbarService.rollbar.error(RollbarExceptionNames.SubsessionCreationFailure, { reason: 'empty_response' })
                return false
            }
        }

        if (status !== SubsessionStatus.InProgress) {
            subsessionList.forEach((s: any) => {
                s.userList = []
            })
        }

        for (const { userId: wseUserId, roomId } of usersRooms) {
            const subsession = subsessionList[roomId]
            if (!subsession) continue

            const participant = conference.participants.find((p: any) => p.wseUserId === wseUserId)
            if (participant) {
                const zoomUserId = Number(participant.clientUserId)
                if (!Number.isNaN(zoomUserId)) {
                    if (status === SubsessionStatus.InProgress) {
                        await ssClient.assignUserToSubsession(zoomUserId, subsession.subsessionId)
                    } else {
                        if (!subsession.userList) subsession.userList = []
                        subsession.userList.push({ userId: zoomUserId })
                    }
                }
            }
        }

        await new Promise(resolve => setTimeout(resolve, SUBSESSION_DELAYS.OPEN_WAIT_MS))

        if (status !== SubsessionStatus.InProgress) {
            await ssClient.openSubsessions(subsessionList, {
                isAutoJoinSubsession: true,
                isBackToMainSessionEnabled: true,
                isAutoMoveBackToMainSession: true,
                waitSeconds: 3
            })
        }

        return true
    } catch (error) {
        rollbarService.rollbar.error(RollbarExceptionNames.SubsessionStartFailed, {
            error: error instanceof Error ? error.message : String(error)
        })
        return false
    }
}

/**
 * Orchestrates the subsession stop sequence.
 *
 * Matches Zoom videosdk-web-sample: `closeAllSubsessions()` only.
 * Avoids `moveBackToMainSession` (host-only; staff often get 5006 INSUFFICIENT_PRIVILEGES)
 * and `leaveSubsession` before close — the SDK returns participants when subsessions end.
 */
export const stopSubsessionWorkflow = async (params: {
    ssClient: any
    isTeacher: boolean
}): Promise<void> => {
    const { ssClient, isTeacher } = params
    if (!ssClient || !isTeacher) return

    try {
        await ssClient.closeAllSubsessions()
    } catch (error) {
        // Retry: Attempt to close subsessions again after a delay
        // This prevents students from getting stuck if the teacher already left
        try {
            await new Promise(resolve => setTimeout(resolve, SUBSESSION_DELAYS.RETRY_DELAY_MS))
            await ssClient.closeAllSubsessions()
        } catch (retryError) {
            rollbarService.rollbar.error(RollbarExceptionNames.SubsessionStopRetryFailed, {
                error: retryError instanceof Error ? retryError.message : String(retryError)
            })
        }
    }
}

/**
 * Orchestrates a teacher's movement between rooms.
 */
export const switchTeacherSubsessionWorkflow = async (params: {
    targetRoom: number | null
    conference: any
    ssClient: any
}): Promise<boolean> => {
    const { targetRoom, ssClient } = params
    if (!ssClient) return false

    try {

        if (targetRoom === null) {
            await ssClient.leaveSubsession()
        } else {
            const subsessionList = ssClient.getSubsessionList() || []
            const targetSubsession = subsessionList[targetRoom]
            if (targetSubsession) {
                await ssClient.joinSubsession(targetSubsession.subsessionId)
            } else {
                return false
            }
        }

        await new Promise(resolve => setTimeout(resolve, SUBSESSION_DELAYS.TEACHER_TRANSITION_MS))
        return true
    } catch (error) {
        rollbarService.rollbar.error(RollbarExceptionNames.SubsessionTeacherSwitchFailed, {
            error: error instanceof Error ? error.message : String(error)
        })
        return false
    }
}

/**
 * Orchestrates a manual join to a subsession (recovery workflow).
 */
export const joinSubsessionWorkflow = async (ssClient: any, subsessionId: string): Promise<boolean> => {
    if (!ssClient || !subsessionId) return false

    try {
        await ssClient.joinSubsession(subsessionId)
        return true
    } catch (error) {
        rollbarService.rollbar.error(RollbarExceptionNames.SubsessionJoinFailure, {
            subsessionId,
            error: error instanceof Error ? error.message : String(error)
        })
        return false
    }
}

/**
 * Type guard for Zoom user displayName metadata
 */
interface ZoomUserMetadata {
    id: string
    role?: string
    fullName?: string
}

const isZoomUserMetadata = (value: unknown): value is ZoomUserMetadata => {
    if (typeof value !== 'object' || value === null) return false
    const obj = value as Record<string, unknown>
    return typeof obj.id === 'string'
}

/**
 * Helper: Find Zoom userId by searching current participants (via live snapshot), all subsessions, and unassigned list.
 */
const findZoomUserId = (ssClient: any, wseUserId: WseUserId): number | null => {
    // 1. Try searching through all subsessions
    const subsessionList = ssClient.getSubsessionList() || []
    for (const subsession of subsessionList) {
        for (const user of subsession.userList || []) {
            try {
                const parsed: unknown = JSON.parse(user.displayName)
                if (isZoomUserMetadata(parsed) && parsed.id === wseUserId) {
                    return user.userId
                }
            } catch {
                if (user.displayName === wseUserId) return user.userId
            }
        }
    }

    return null
}

/**
 * Functional Orchestrator: Moves a single user to a target room.
 */
export const moveUserWorkflow = async (params: {
    wseUserId: WseUserId
    toRoomId: number
    ssClient: any
    conference: any
}): Promise<void> => {
    const { wseUserId, toRoomId, ssClient } = params
    if (!ssClient) return

    const subsessionList = ssClient.getSubsessionList() || []
    const expectedName = `Room ${String.fromCharCode(65 + toRoomId)}`
    const targetSubsession = subsessionList.find((s: any) => s.subsessionName === expectedName)

    if (!targetSubsession?.subsessionId) return

    const zoomUserId = findZoomUserId(ssClient, wseUserId)

    if (zoomUserId === null) {
        return
    }

    await ssClient.moveUserToSubsession(zoomUserId, targetSubsession.subsessionId)
}

/**
 * Functional Orchestrator: Handles bulk user swaps between rooms.
 * Uses sequential iteration to avoid race conditions in the SDK.
 */
export const swapUsersWorkflow = async (params: {
    swappedUsers: Array<{ userId: WseUserId; toRoomId: number | null }>
    ssClient: any
    conference: any
}): Promise<boolean> => {
    const { swappedUsers, ssClient, conference } = params
    if (!swappedUsers.length || !ssClient) return true

    try {
        for (const user of swappedUsers) {
            if (user.toRoomId !== null) {
                await moveUserWorkflow({
                    wseUserId: user.userId,
                    toRoomId: user.toRoomId,
                    ssClient,
                    conference
                })
                // Sequential execution with small delay to prevent SDK congestion
                await new Promise(resolve => setTimeout(resolve, SUBSESSION_DELAYS.SWAP_DELAY_MS))
            }
        }
        return true
    } catch (error) {
        rollbarService.rollbar.error(RollbarExceptionNames.SubsessionSwapUsersFailed, {
            error: error instanceof Error ? error.message : String(error)
        })
        return false
    }
}

/**
 * Functional Orchestrator: Assigns pure late-joining students (no prior subsession assignment)
 * to the least-populated active room.
 *
 * This workflow is intentionally simple — it only handles users who are completely new
 * to the session and have no Zoom subsession history. Re-joining after disconnection
 * is handled separately.
 */
export const handleLateJoinersWorkflow = async (params: {
    unassignedUsers: any[]
    ssClient: any
}): Promise<Array<{ userId: WseUserId; roomId: number }>> => {
    const { unassignedUsers, ssClient } = params
    const results: Array<{ userId: WseUserId; roomId: number }> = []

    if (!ssClient || !unassignedUsers.length) return results

    try {
        const subsessionList: any[] = ssClient.getSubsessionList() || []
        if (subsessionList.length === 0) {
            return results
        }

        for (const user of unassignedUsers) {
            const zoomUserId: number = user.userId

            // Resolve WSE user ID from displayName (JSON-encoded metadata)
            let wseUserId: string = ''
            try {
                const parsed: unknown = JSON.parse(user.displayName)
                if (isZoomUserMetadata(parsed)) {
                    wseUserId = parsed.id
                }
            } catch {
                // displayName is not JSON — skip this user
            }

            if (!wseUserId) continue

            // Find the active room with the fewest users
            const roomsByOccupancy = subsessionList
                .map((subsession: any, index: number) => ({
                    index,
                    subsessionId: subsession.subsessionId,
                    subsessionName: subsession.subsessionName,
                    numUsers: subsession.userList?.length || 0
                }))
                .filter(room => room.numUsers > 0) // Only consider rooms that are already active

            const targetRoom = roomsByOccupancy.length > 0
                ? roomsByOccupancy.reduce((prev, curr) => curr.numUsers < prev.numUsers ? curr : prev)
                : { index: 0, subsessionId: subsessionList[0]?.subsessionId, subsessionName: subsessionList[0]?.subsessionName }

            if (!targetRoom.subsessionId) {
                continue
            }

            await ssClient.assignUserToSubsession(zoomUserId, targetRoom.subsessionId)
            results.push({ userId: wseUserId, roomId: targetRoom.index })
        }
    } catch (error) {
        rollbarService.rollbar.error(RollbarExceptionNames.SubsessionLateJoinersFailed, {
            error: error instanceof Error ? error.message : String(error)
        })
    }

    return results
}

/**
 * Helper: Extracts participant data from a Zoom subsession user list.
 */
export const extractParticipantDataFromZoomUsers = (users: any[]): Array<{ id: string; firstName: string; lastName: string }> => {
    const participants: Array<{ id: string; firstName: string; lastName: string }> = []
    users.forEach(user => {
        try {
            const parsed = JSON.parse(user.displayName)
            if (parsed.role !== 'teacher') {
                let firstName = parsed.fullName || parsed.id
                let lastName = ''
                const spaceIdx = firstName.indexOf(' ')
                if (spaceIdx !== -1) {
                    lastName = firstName.substring(spaceIdx + 1)
                    firstName = firstName.substring(0, spaceIdx)
                }
                participants.push({
                    id: parsed.id,
                    firstName,
                    lastName
                })
            }
        } catch (e) {
            // Ignore parse errors
        }
    })
    return participants
}

/**
 * Helper: Extracts fallback participant data from active Subsessions if a user is physically isolated.
 */
export const getParticipantFallbackFromSubsession = (
    userId: WseUserId,
    conference: any
): ConferenceParticipant | null => {
    if (conference.status !== 'session-joined') return null

    const studentInfo = conference.classroomStateOnJoin?.dcAppData?.students?.find((s: any) => s.id === userId)
    const ssClient = conference.rtcSession?.getSubsessionClient?.()

    if (ssClient && ssClient.getSubsessionStatus() === SubsessionStatus.InProgress) {
        const subsessionList = ssClient.getSubsessionList() || []
        for (const subsession of subsessionList) {
            const extracted = extractParticipantDataFromZoomUsers(subsession.userList || [])
            const zoomUser = extracted.find(u => u.id === userId)
            if (zoomUser) {
                return {
                    clientUserId: `fallback-${userId}`,
                    cameraStatus: 'off',
                    audioStatus: 'off',
                    lastName: studentInfo?.lastName || zoomUser.lastName || '',
                    firstName: studentInfo?.firstName || zoomUser.firstName || 'Student',
                    wseProfilePhotoUrl: studentInfo?.photoUri,
                    wseUserId: userId,
                    role: 'student',
                    micHardMuted: false,
                    expelled: false
                }
            }
        }
    }
    return null
}
